import React__default from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useGoogleMapsContext, Util, MediaQuery, InfoWindow } from '@grandvisionhq/common';
import { LocationSearchMap } from '@grandvisionhq/locations';
import { useIntl, useClickAndReserveFeatureConfig } from '@grandvisionhq/state';
import { storesLocatorResultEvent } from '../../data-layer/actions.js';
import StoreGroupFinderItem from '../StoreFinderItem/StoreFinderItemContainer.js';
import CurrentLocationPin from './CurrentLocationPin.js';
import PreferredStorePin from './PreferredStorePin.js';
import { useStoreFinderContext } from '../../context/StoreFinder/context.js';
import { hasStock } from '../../utils/has-stock.js';
import { isEqualStores } from '../../utils/store-utils.js';

const currentLocationPin = CurrentLocationPin({
    theme: {
        borderColor: '#2B2E31',
        background: '#fff'
    }
});
const preferredStorePinFallback = PreferredStorePin();
const StoreFinderMapContainer = ({ stores , infoWindowContentComponent: InfoWindowContentComponent , buttonsComponent: ButtonsComponent , showAttributesAsTags  })=>{
    const { hoveredStore: hoveredStoreInList , config , isUsingUserLocation , userLocation , isUserLocationRequested , setIsUserLocationRequested , events , preferredStore: preferredStore1 , setActiveMapInfoWindow , activeMapInfoWindow ,  } = useStoreFinderContext();
    const { googleMapsSettings: { preferredStorePin  } ,  } = useGoogleMapsContext();
    const { getLabel  } = useIntl();
    const { googleMapsId  } = config;
    const [activeStore, setActiveStore] = React__default.useState(null);
    const clickAndReserveMode = useClickAndReserveFeatureConfig();
    const prevStores = Util.usePrevious(stores);
    const isPhone = MediaQuery.useIsPhone();
    const [hoveredStore, setHoveredStore] = React__default.useState(null);
    const handleOnMarkerHover = (code)=>{
        setHoveredStore(code);
    };
    const handleOnClickUseMyLocation = React__default.useCallback(()=>{
        if (!isUserLocationRequested) {
            setIsUserLocationRequested(true);
        }
        if (clickAndReserveMode) {
            var ref;
            events === null || events === void 0 ? void 0 : (ref = events.onClickLocationEvent) === null || ref === void 0 ? void 0 : ref.call(events, clickAndReserveMode, 'map');
        }
    }, [
        isUserLocationRequested,
        events,
        clickAndReserveMode,
        setIsUserLocationRequested
    ]);
    const handleOnMarkerClick = React__default.useCallback((code)=>{
        if (activeMapInfoWindow === code) {
            setActiveMapInfoWindow(null);
            setActiveStore(null);
            return;
        }
        // We need to expand the list of stores with a preferredStore if present
        const storesWithPreferredStore = preferredStore1 ? [
            ...stores,
            preferredStore1
        ] : stores;
        const store1 = storesWithPreferredStore.find((store)=>store.code === code
        );
        if (store1) {
            const dataLayerEvent = storesLocatorResultEvent({
                store: store1,
                preferredStore: (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code) === store1.code,
                interaction: 'map click - marker',
                searchResults: stores.length
            });
            DataLayer.pushEvent(dataLayerEvent);
            setActiveMapInfoWindow(code);
            setActiveStore(store1);
        }
    }, [
        activeMapInfoWindow,
        setActiveMapInfoWindow,
        preferredStore1,
        stores
    ]);
    const closeInfoWindow = React__default.useCallback(()=>{
        setActiveMapInfoWindow(null);
        setActiveStore(null);
    }, [
        setActiveMapInfoWindow,
        setActiveStore
    ]);
    const InfoWindowContent = InfoWindowContentComponent !== null && InfoWindowContentComponent !== void 0 ? InfoWindowContentComponent : StoreGroupFinderItem;
    const preferredStoreMarkerConfig = React__default.useCallback((preferredStore, excludedFromMapBehavior = true)=>{
        const isInactive = !!(preferredStore === null || preferredStore === void 0 ? void 0 : preferredStore.stock) && !hasStock(preferredStore.stock);
        return {
            code: preferredStore.code,
            title: getLabel('stores.component.storeFinderMap.map.pin.title'),
            position: {
                lat: Number(preferredStore.lat),
                lng: Number(preferredStore.lon)
            },
            pin: preferredStorePin || preferredStorePinFallback,
            inactive: isInactive,
            infoWindow: activeMapInfoWindow === preferredStore.code && !isPhone && /*#__PURE__*/ React__default.createElement(InfoWindow, {
                position: {
                    lat: Number(preferredStore.lat),
                    lng: Number(preferredStore.lon)
                },
                options: {
                    pixelOffset: {
                        width: 0,
                        height: -4
                    }
                },
                onCloseClick: closeInfoWindow
            }, /*#__PURE__*/ React__default.createElement(InfoWindowContent, {
                className: "store-finder-map__info-window-contents",
                store: preferredStore,
                view: "map",
                buttons: ButtonsComponent ? /*#__PURE__*/ React__default.createElement(ButtonsComponent, {
                    store: preferredStore,
                    view: "map"
                }) : null,
                isPreferredStore: true,
                showAttributesAsTags: showAttributesAsTags
            })),
            excludedFromMapBehavior
        };
    }, [
        ButtonsComponent,
        InfoWindowContent,
        activeMapInfoWindow,
        closeInfoWindow,
        getLabel,
        isPhone,
        preferredStorePin,
        showAttributesAsTags, 
    ]);
    const storeMarkers = React__default.useMemo(()=>{
        return stores.map((store)=>{
            const { code , lat , lon  } = store;
            const isInactive = !!(store === null || store === void 0 ? void 0 : store.stock) && !hasStock(store.stock);
            const storeIsPreferredStore = store.code === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code);
            if (storeIsPreferredStore) {
                return preferredStoreMarkerConfig(store, false);
            }
            return {
                code,
                title: getLabel('stores.component.storeFinderMap.map.pin.title'),
                optimized: false,
                position: {
                    lat: Number(lat),
                    lng: Number(lon)
                },
                inactive: isInactive,
                infoWindow: activeMapInfoWindow === code && !isPhone && /*#__PURE__*/ React__default.createElement(InfoWindow, {
                    position: {
                        lat: Number(lat),
                        lng: Number(lon)
                    },
                    options: {
                        pixelOffset: {
                            width: 0,
                            height: -4
                        }
                    },
                    onCloseClick: closeInfoWindow
                }, /*#__PURE__*/ React__default.createElement(InfoWindowContent, {
                    className: "store-finder-map__info-window-contents",
                    store: store,
                    view: "map",
                    buttons: ButtonsComponent ? /*#__PURE__*/ React__default.createElement(ButtonsComponent, {
                        store: store,
                        view: "map"
                    }) : null,
                    showAttributesAsTags: showAttributesAsTags
                }))
            };
        });
    }, [
        ButtonsComponent,
        InfoWindowContent,
        activeMapInfoWindow,
        closeInfoWindow,
        getLabel,
        isPhone,
        preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code,
        preferredStoreMarkerConfig,
        stores,
        showAttributesAsTags, 
    ]);
    const userLocationMarker = React__default.useMemo(()=>{
        if (isUsingUserLocation && userLocation) {
            return [
                {
                    position: {
                        lat: userLocation.lat,
                        lng: userLocation.lng
                    },
                    pin: currentLocationPin
                }, 
            ];
        }
        return [];
    }, [
        isUsingUserLocation,
        userLocation
    ]);
    const preferredStoreMarker = React__default.useMemo(()=>{
        const preferredStoreFoundInList = !!stores.filter((store)=>{
            return store.code === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.code);
        }).length;
        // Only add preferred store marker here if not present in search results
        if (preferredStore1 && !preferredStoreFoundInList) {
            return [
                preferredStoreMarkerConfig(preferredStore1)
            ];
        }
        return [];
    }, [
        preferredStore1,
        preferredStoreMarkerConfig,
        stores
    ]);
    const markers = React__default.useMemo(()=>{
        return [
            ...storeMarkers,
            ...userLocationMarker,
            ...preferredStoreMarker
        ];
    }, [
        preferredStoreMarker,
        storeMarkers,
        userLocationMarker
    ]);
    const center = React__default.useMemo(()=>{
        if (isUsingUserLocation && userLocation !== undefined) {
            return userLocation;
        }
        return undefined;
    }, [
        userLocation,
        isUsingUserLocation
    ]);
    React__default.useEffect(()=>{
        if (prevStores && !isEqualStores(prevStores, stores)) {
            setActiveStore(null);
            setActiveMapInfoWindow(null);
        }
    }, [
        stores,
        prevStores,
        setActiveMapInfoWindow
    ]);
    var ref1;
    return /*#__PURE__*/ React__default.createElement(LocationSearchMap, {
        id: googleMapsId,
        markers: markers,
        markersUpdateConfig: {
            centerToFirst: true,
            zoom: 13
        },
        center: center,
        highlight: (ref1 = activeStore === null || activeStore === void 0 ? void 0 : activeStore.code) !== null && ref1 !== void 0 ? ref1 : null,
        hovered: hoveredStore !== null && hoveredStore !== void 0 ? hoveredStore : hoveredStoreInList === null || hoveredStoreInList === void 0 ? void 0 : hoveredStoreInList.code,
        userLocation: isUsingUserLocation && userLocation ? userLocation : undefined,
        userLocationZoomLevel: 13,
        centerOnUserLocation: true,
        activeLocation: isPhone && activeStore ? /*#__PURE__*/ React__default.createElement("div", {
            className: "store-finder-map__active-location"
        }, activeStore ? /*#__PURE__*/ React__default.createElement(InfoWindowContent, {
            className: "store-finder-map__active-location-contents",
            store: activeStore,
            buttons: ButtonsComponent ? /*#__PURE__*/ React__default.createElement(ButtonsComponent, {
                store: activeStore,
                view: "map"
            }) : null,
            view: "map",
            showAttributesAsTags: showAttributesAsTags
        }) : null) : null,
        onClickUseMyLocation: handleOnClickUseMyLocation,
        onMarkerClick: handleOnMarkerClick,
        onMarkerHover: handleOnMarkerHover,
        onMapClick: closeInfoWindow
    });
};
var StoreFinderMapContainer$1 = StoreFinderMapContainer;

export { StoreFinderMapContainer$1 as default };
//# sourceMappingURL=StoreFinderMapContainer.js.map
